import {
  createApiRef,
  DiscoveryApi,
  FetchApi,
} from '@backstage/core-plugin-api';
import type {
  AccessResponse,
  AddMembersRequest,
  AddMembersResponse,
  ArmoryGroup,
  AuditResponse,
  GroupMember,
  GroupsResponse,
  ResolveEmailsResponse,
  SetPauseRequest,
  SetPermissionsRequest,
  ToolsResponse,
} from '@internal/plugin-armory-common';

/** Each list matches any of its values; leave it out (or empty) for any. */
export type AuditFilters = {
  person?: string;
  groups?: string[];
  tools?: string[];
  actions?: string[];
};

export class AdminApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    /** The backend's code (forbidden, conflict, ...) or ours: network, gateway, bad_response. */
    readonly code: string,
    readonly retryable: boolean,
  ) {
    super(message);
    this.name = 'AdminApiError';
  }
}

export interface AdminApi {
  getAccess(): Promise<AccessResponse>;
  listGroups(): Promise<GroupsResponse>;
  createGroup(group: {
    name: string;
    description?: string;
  }): Promise<ArmoryGroup>;
  updateGroup(
    id: string,
    changes: { name?: string; description?: string },
  ): Promise<ArmoryGroup>;
  deleteGroup(id: string): Promise<void>;
  listMembers(groupId: string): Promise<{ members: GroupMember[] }>;
  addMembers(
    groupId: string,
    request: AddMembersRequest,
  ): Promise<AddMembersResponse>;
  removeMember(groupId: string, ref: string): Promise<void>;
  resolveEmails(text: string): Promise<ResolveEmailsResponse>;
  listTools(): Promise<ToolsResponse>;
  setPermissions(toolId: string, request: SetPermissionsRequest): Promise<void>;
  setPause(toolId: string, request: SetPauseRequest): Promise<void>;
  /** Newest first; `limit` up to the backend's page cap (200). */
  listAudit(
    filters: AuditFilters,
    cursor?: string,
    limit?: number,
  ): Promise<AuditResponse>;
}

export const adminApiRef = createApiRef<AdminApi>({
  id: 'plugin.armory.admin',
});

const enc = encodeURIComponent;

export class AdminClient implements AdminApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(options: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
  }

  getAccess(): Promise<AccessResponse> {
    return this.request('GET', '/access');
  }

  listGroups(): Promise<GroupsResponse> {
    return this.request('GET', '/admin/groups');
  }

  createGroup(group: {
    name: string;
    description?: string;
  }): Promise<ArmoryGroup> {
    return this.request('POST', '/admin/groups', group);
  }

  updateGroup(
    id: string,
    changes: { name?: string; description?: string },
  ): Promise<ArmoryGroup> {
    return this.request('PATCH', `/admin/groups/${enc(id)}`, changes);
  }

  async deleteGroup(id: string): Promise<void> {
    await this.request('DELETE', `/admin/groups/${enc(id)}`);
  }

  listMembers(groupId: string): Promise<{ members: GroupMember[] }> {
    return this.request('GET', `/admin/groups/${enc(groupId)}/members`);
  }

  addMembers(
    groupId: string,
    request: AddMembersRequest,
  ): Promise<AddMembersResponse> {
    return this.request(
      'POST',
      `/admin/groups/${enc(groupId)}/members`,
      request,
    );
  }

  // The ref goes in the query string: entity refs contain "/"
  async removeMember(groupId: string, ref: string): Promise<void> {
    await this.request(
      'DELETE',
      `/admin/groups/${enc(groupId)}/members?ref=${enc(ref)}`,
    );
  }

  resolveEmails(text: string): Promise<ResolveEmailsResponse> {
    return this.request('POST', '/admin/resolve-emails', { text });
  }

  listTools(): Promise<ToolsResponse> {
    return this.request('GET', '/admin/tools');
  }

  async setPermissions(
    toolId: string,
    request: SetPermissionsRequest,
  ): Promise<void> {
    await this.request(
      'PUT',
      `/admin/tools/${enc(toolId)}/permissions`,
      request,
    );
  }

  async setPause(toolId: string, request: SetPauseRequest): Promise<void> {
    await this.request('PUT', `/admin/tools/${enc(toolId)}/pause`, request);
  }

  listAudit(
    filters: AuditFilters,
    cursor?: string,
    limit?: number,
  ): Promise<AuditResponse> {
    const params = new URLSearchParams();
    if (filters.person) params.set('person', filters.person);
    // Repeated keys, one per value: ?tool=a&tool=b
    for (const [key, values] of [
      ['group', filters.groups],
      ['tool', filters.tools],
      ['action', filters.actions],
    ] as const) {
      for (const value of values ?? []) params.append(key, value);
    }
    if (cursor) params.set('cursor', cursor);
    if (limit) params.set('limit', String(limit));
    const query = params.toString();
    return this.request('GET', `/admin/audit${query ? `?${query}` : ''}`);
  }

  private async request<T>(
    method: string,
    path: string,
    body?: unknown,
  ): Promise<T> {
    const url = `${await this.discoveryApi.getBaseUrl('armory')}${path}`;
    let res: Response;
    try {
      res = await this.fetchApi.fetch(url, {
        method,
        ...(body !== undefined && {
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        }),
      });
    } catch {
      throw new AdminApiError(
        "Couldn't reach Backstage. Check your connection and try again.",
        0,
        'network',
        true,
      );
    }
    if (!res.ok) throw await toAdminError(res);
    if (res.status === 204) return undefined as T;
    try {
      return (await res.json()) as T;
    } catch {
      throw new AdminApiError(
        'Backstage sent a response that could not be read. Try again.',
        res.status,
        'bad_response',
        true,
      );
    }
  }
}

const GATEWAY_STATUSES = new Set([502, 503, 504]);

async function toAdminError(res: Response): Promise<AdminApiError> {
  const body = await res.json().catch(() => undefined);
  const error = body?.error ?? {};
  const code = typeof error.code === 'string' ? error.code : undefined;
  // Gateway errors from the load balancer arrive without our envelope.
  if (!code && GATEWAY_STATUSES.has(res.status)) {
    return new AdminApiError(
      'Backstage is temporarily unavailable. Try again.',
      res.status,
      'gateway',
      true,
    );
  }
  return new AdminApiError(
    typeof error.message === 'string' && error.message
      ? error.message
      : `The request failed (HTTP ${res.status}).`,
    res.status,
    code ?? 'unknown',
    error.retryable === true,
  );
}
